import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { EcaEventNode }     from './nodes/EcaEventNode.jsx';
import { EcaActionNode }    from './nodes/EcaActionNode.jsx';
import { EcaConditionNode } from './nodes/EcaConditionNode.jsx';
import { EcaGatewayNode }   from './nodes/EcaGatewayNode.jsx';
import { EcaVariableNode }  from './nodes/EcaVariableNode.jsx';
import { EcaPluginPicker }  from './EcaPluginPicker.jsx';
import { NodeConfigPanel }  from './panels/NodeConfigPanel.jsx';
import { ExecutionMockProvider } from './mock/ExecutionMockContext.jsx';
import { MOCK_MODEL } from './mock/ecaMock.js';
import { DRUPAL_BASE_URL } from '../../config/drupal.js';
import { fromEca, toEca } from './ecaConverter.js';
import { EcaPluginCatalogueProvider, useCatalogue } from './EcaPluginCatalogueContext.jsx';
import { ZoomScaleBridge } from './EcaHandleLabel.jsx';
import { nodeColorKey } from './ecaNodeVisuals.js';
import { getLayoutedElements } from './ecaLayout.js';
import {
  VariablesCtx,
  VAR_PALETTE,
  genVarId,
  varIdToColor,
} from './EcaVariablesContext.jsx';

// ---------------------------------------------------------------------------
// Edge geometry
// ---------------------------------------------------------------------------

/** Bezier bow of the flux edges (forwarded to getBezierPath). */
const FLUX_CURVATURE = 1;
/** Bezier bow of the data edges (scales the vertical-stem bulge). */
const DATA_CURVATURE = 1;

// ---------------------------------------------------------------------------
// Custom edge types
// ---------------------------------------------------------------------------

/**
 * Connection line preview — matches the curvature and path type of the
 * actual edges so the preview is visually consistent with the final result.
 * Detects flux vs. data based on the source handle ID.
 */
const CustomConnectionLine = ({ fromX, fromY, toX, toY, fromPosition, toPosition, fromHandle }) => {
  const isData = fromHandle?.id?.startsWith('data-') || fromHandle?.id === 'var-out';

  let d;
  if (isData) {
    // Mirror of DataEdge: vertical stems + dx-scaled bezier bow.
    // Stem direction depends on the handle side: BOTTOM exits down, TOP exits up.
    const fromBottom = fromPosition === 'bottom';
    const p1y = fromY + (fromBottom ?  DATA_EDGE_STEM : -DATA_EDGE_STEM);
    const p2y = toY   + (fromBottom ? -DATA_EDGE_STEM :  DATA_EDGE_STEM);
    const mid = Math.min(Math.abs(toX - fromX), 460) * DATA_CURVATURE;
    // Control points extend in the same direction as the stems so tangents stay vertical.
    const c1y = fromBottom ? p1y + mid : p1y - mid;
    const c2y = fromBottom ? p2y - mid : p2y + mid;
    d = [
      `M ${fromX} ${fromY}`,
      `L ${fromX} ${p1y}`,
      `C ${fromX} ${c1y} ${toX} ${c2y} ${toX} ${p2y}`,
      `L ${toX} ${toY}`,
    ].join(' ');
  } else {
    const [flux] = getBezierPath({
      sourceX: fromX, sourceY: fromY, sourcePosition: fromPosition,
      targetX: toX,   targetY: toY,   targetPosition: toPosition,
      curvature: FLUX_CURVATURE,
    });
    d = flux;
  }

  const stroke    = isData ? '#10B981' : '#3B82F6';
  const dasharray = isData ? '5 3' : undefined;
  const width     = isData ? 1.5 : 2.5;

  return (
    <g>
      <path fill="none" stroke={stroke} strokeWidth={width}
        strokeDasharray={dasharray} d={d} />
    </g>
  );
};

/** Hook that tracks hover state with a short hide-delay so the cursor
 *  can move from the SVG edge to the HTML button without flickering. */
const useEdgeHover = () => {
  const [hovered, setHovered] = useState(false);
  const timer = useRef(null);
  const onEnter = () => { clearTimeout(timer.current); setHovered(true); };
  const onLeave = () => { timer.current = setTimeout(() => setHovered(false), 150); };
  return { hovered, onEnter, onLeave };
};

const EdgeDeleteButton = ({ id, labelX, labelY, onEnter, onLeave }) => {
  const { deleteElements } = useReactFlow();
  return (
    <EdgeLabelRenderer>
      <div
        style={{
          transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          position: 'absolute',
          pointerEvents: 'all',
        }}
        className="nodrag nopan"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <button
          onClick={(e) => { e.stopPropagation(); deleteElements({ edges: [{ id }] }); }}
          className="w-5 h-5 bg-white border-2 border-red-400 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center shadow-sm text-xs font-bold leading-none transition-opacity"
          title="Delete connection"
        >
          ×
        </button>
      </div>
    </EdgeLabelRenderer>
  );
};

const FluxEdge = ({ id, data, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, selected }) => {
  const [computedPath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: FLUX_CURVATURE,
  });
  const edgePath = data?.elkPath ?? computedPath;
  const { hovered, onEnter, onLeave } = useEdgeHover();
  const visible = hovered || selected;
  return (
    <>
      <g onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <BaseEdge
          id={id}
          path={edgePath}
          style={{ stroke: visible ? '#1D4ED8' : '#3B82F6', strokeWidth: visible ? 3 : 2.5, ...style }}
        />
        {/* Wider transparent hit area for easier hover */}
        <path d={edgePath} strokeWidth={16} stroke="transparent" fill="none" />
      </g>
      {visible && <EdgeDeleteButton id={id} labelX={labelX} labelY={labelY} onEnter={onEnter} onLeave={onLeave} />}
    </>
  );
};

const DATA_EDGE_STEM = 20;

const DataEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, selected }) => {
  // Path: vertical stem down → cubic bezier → vertical stem up.
  //
  //  (sourceX, sourceY)              BOTTOM handle
  //       │  ← L (vertical, STEM px)
  //  (sourceX, sourceY + STEM)       P1
  //       ╲  cubic bezier            control points stay on same X as P1/P2
  //  (targetX, targetY - STEM)       P2
  //       │  ← L (vertical, STEM px)
  //  (targetX, targetY)              TOP handle
  //
  // Tangent is vertical at P1 and P2 → seamless join with the straight stems.
  const p1y = sourceY + DATA_EDGE_STEM;
  const p2y = targetY - DATA_EDGE_STEM;
  // Bulge scales with horizontal offset: perfectly vertical wires stay straight,
  // wires with lateral offset curve smoothly up to 80 px of bezier bow.
  const mid = Math.min(Math.abs(targetX - sourceX), 460) * DATA_CURVATURE;
  const edgePath = [
    `M ${sourceX} ${sourceY}`,
    `L ${sourceX} ${p1y}`,
    `C ${sourceX} ${p1y + mid} ${targetX} ${p2y - mid} ${targetX} ${p2y}`,
    `L ${targetX} ${targetY}`,
  ].join(' ');
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;
  const { hovered, onEnter, onLeave } = useEdgeHover();
  const visible = hovered || selected;

  return (
    <>
      <g onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <BaseEdge
          id={id}
          path={edgePath}
          style={{ stroke: visible ? '#059669' : '#10B981', strokeWidth: visible ? 2 : 1.5, strokeDasharray: '5 3', ...style }}
        />
        {/* Wider transparent hit area for easier hover */}
        <path d={edgePath} strokeWidth={16} stroke="transparent" fill="none" />
      </g>

      {visible && <EdgeDeleteButton id={id} labelX={labelX} labelY={labelY} onEnter={onEnter} onLeave={onLeave} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// Auto-layout (Dagre) — voir ecaLayout.js
// ---------------------------------------------------------------------------

/**
 * Marqueur écrit dans third_party_settings.eca_react_editor à chaque
 * enregistrement depuis cette interface : sa présence signifie « la mise en
 * page vient de l'utilisateur, ne pas la refaire à l'ouverture ».
 */
const LAYOUT_MANUAL = 'manual';

/** Garde-fou (ms) si ReactFlow ne mesure jamais tous les noeuds. */
const AUTO_LAYOUT_MEASURE_TIMEOUT = 3000;

// ---------------------------------------------------------------------------
// Node and edge type maps
// ---------------------------------------------------------------------------

// Exported so the perf benchmark (panPerf.browser.test.jsx) can render the real
// node/edge components without duplicating the maps.
export const NODE_TYPES = {
  event:     EcaEventNode,
  action:    EcaActionNode,
  condition: EcaConditionNode,
  gateway:   EcaGatewayNode,
  variable:  EcaVariableNode,
};

export const EDGE_TYPES = {
  flux: FluxEdge,
  data: DataEdge,
};

// Stable prop objects for <ReactFlow> — kept at module scope so their identity
// never changes across renders (a new ref each render churns RF's internal store).
const FIT_VIEW_OPTIONS     = { padding: 0.2 };
const DEFAULT_EDGE_OPTIONS = { type: 'flux' };
const DELETE_KEY_CODES     = ['Backspace', 'Delete'];
// Couleurs de la minimap — mêmes teintes 'active' que NODE_COLORS ; les actions
// ECA REST (appel HTTP sortant) y ressortent en orange comme dans le canvas.
const MINIMAP_TYPE_COLORS  = { event: '#7C3AED', action: '#2563EB', restAction: '#C2410C', condition: '#D97706', gateway: '#E11D48' };

// ---------------------------------------------------------------------------
// ID generators
// ---------------------------------------------------------------------------

const genId = (prefix) => `${prefix}_${crypto.randomUUID().slice(0, 8)}`;

// ---------------------------------------------------------------------------
// Default node data per type
// ---------------------------------------------------------------------------

// defaultNodeData is defined inside EcaEditorInner so it can close over
// getPluginDef from the catalogue context. See usage below.

// ---------------------------------------------------------------------------
// Context menu component
// ---------------------------------------------------------------------------

const ContextMenu = ({ menu, onAddVariable, onDuplicateRef, onDeleteRef, onDeleteVariable, onClose }) => {
  if (!menu) return null;

  return (
    <>
      {/* Invisible backdrop to close on click outside */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />
      <div
        style={{
          position: 'fixed',
          left: menu.x,
          top: menu.y,
          zIndex: 1000,
        }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[180px] text-sm"
        onContextMenu={(e) => e.preventDefault()}
      >
        {menu.type === 'canvas' && (
          <button
            onClick={onAddVariable}
            className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700 flex items-center gap-2"
          >
            <span className="text-iris-500 font-bold">◉</span>
            Add variable
          </button>
        )}

        {menu.type === 'variable' && (
          <>
            <button
              onClick={onDuplicateRef}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700 flex items-center gap-2"
            >
              <span className="text-gray-400">⊕</span>
              Duplicate reference
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={onDeleteRef}
              className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-500 flex items-center gap-2"
            >
              <span>✕</span>
              Delete this reference
            </button>
            <button
              onClick={onDeleteVariable}
              className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 font-semibold flex items-center gap-2"
            >
              <span>⊘</span>
              Delete variable (all)
            </button>
          </>
        )}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Inner editor (needs ReactFlowProvider context)
// ---------------------------------------------------------------------------

const EMPTY_DATA = { nodes: [], edges: [], variables: {} };

const EcaEditorInner = ({ modelId: embeddedModelId, onBack, embedded = false }) => {
  // Monte AUSSI hors de sa route : le panneau « Processus » du builder l'embarque
  // en plein écran pour montrer le flux qui traite un formulaire. Il reçoit
  // alors son modèle en propriété, et un `onBack` qui referme la pop-in au lieu
  // de faire quitter au builder la page en cours d'édition.
  const { modelId: routeModelId } = useParams();
  const modelId = embeddedModelId ?? routeModelId;
  const navigate = useNavigate();
  const goBack = onBack ?? (() => navigate('/eca-editor'));
  // Hauteur : plein écran quand il EST l'écran, celle de son hôte quand il y est
  // embarqué — un `100dvh` dans une pop-in de 99vh déborde sous le bord.
  const shellHeight = embedded ? '100%' : '100dvh';
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const { getPluginDef, loading: catalogueLoading } = useCatalogue();

  // Local defaultNodeData closed over getPluginDef from the live catalogue
  const defaultNodeData = (nodeType, pluginId, label) => {
    const pluginCat = { event: 'events', action: 'actions', condition: 'conditions', gateway: 'gateways' }[nodeType];
    const pluginDef = pluginCat ? getPluginDef(pluginCat, pluginId) : null;
    const base = {
      label: label ?? pluginDef?.label ?? nodeType,
      plugin: pluginId ?? '',
      description: pluginDef?.description ?? '',
      configuration: {},
    };
    if (nodeType === 'event' || nodeType === 'action') {
      base.tokenOutputs = pluginDef?.tokenOutputs ?? [];
    }
    if (nodeType === 'gateway') {
      base.branches = [];
    }
    return base;
  };

  const isNew  = modelId === 'new';
  const isReal = modelId && !isNew;

  // Loading state
  const [loadState, setLoadState] = useState(isReal ? 'loading' : 'ready');
  const [modelMeta, setModelMeta] = useState(
    isReal ? null : { id: isNew ? '' : 'mock', label: isNew ? 'Nouveau modèle' : 'Mock model' }
  );

  // Derive the mock graph only when it is actually needed (offline/demo model),
  // and only once — fromEca() is expensive and EcaEditorInner re-renders on
  // every drag frame / keystroke / slider tick. Computing it eagerly here ran
  // fromEca(MOCK_MODEL) on every render even for real models, where the result
  // was discarded after mount (useState only reads its initial value once).
  const mockData = useMemo(
    () => (isReal || isNew ? EMPTY_DATA : fromEca(MOCK_MODEL, { getPluginDef })),
    [isReal, isNew, getPluginDef],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(isReal ? [] : (isNew ? [] : mockData.nodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(isReal ? [] : (isNew ? [] : mockData.edges));
  const [selectedNode, setSelectedNode]   = useState(null);
  const [variables, setVariables]         = useState(isReal ? {} : (isNew ? {} : mockData.variables));
  const [contextMenu, setContextMenu]     = useState(null);
  // Vrai entre le rangement automatique à l'ouverture (hauteurs estimées) et sa
  // reprise une fois les noeuds mesurés par ReactFlow.
  const [autoLayoutPending, setAutoLayoutPending] = useState(false);

  // Load model from API when a real modelId is present.
  // Wait for the plugin catalogue to be ready first so that fromEca() can
  // resolve plugin definitions (tokenOutputs, configSchema) correctly, and
  // React Flow node handles are registered before edges are validated.
  useEffect(() => {
    if (!isReal || catalogueLoading) return;
    setLoadState('loading');
    fetch(`${DRUPAL_BASE_URL}/decoupled-config-api/eca/${modelId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data?.status?.type === 'error') throw new Error(data.status.message);
        const parsed = fromEca(data, { getPluginDef });
        // Un modèle jamais mis en page DANS cet éditeur (flux écrit à la main,
        // importé, ou produit par le harnais) arrive avec des positions
        // arbitraires — souvent toutes à (0,0). On le range automatiquement à
        // l'ouverture ; dès qu'il a été enregistré depuis l'interface, la mise
        // en page de l'utilisateur fait foi (marqueur `layout: 'manual'`).
        const editorData = data.third_party_settings?.eca_react_editor ?? {};
        const autoLayout = editorData.layout !== LAYOUT_MANUAL && parsed.nodes.length > 0;
        setNodes(autoLayout
          ? getLayoutedElements(parsed.nodes, parsed.edges).nodes
          : parsed.nodes);
        setEdges(parsed.edges);
        setVariables(parsed.variables);
        // Deuxième passe une fois les noeuds mesurés par ReactFlow : la
        // première tourne sur des hauteurs estimées et laisse des
        // chevauchements sur les noeuds hauts.
        setAutoLayoutPending(autoLayout);
        // `positions` / `variables` / `layout` sont reconstruits par toEca ; le
        // RESTE de eca_react_editor (les `categories` affectées depuis la liste
        // des modèles) doit être reporté tel quel, sinon la sauvegarde l'efface.
        const { positions: _p, variables: _v, layout: _l, ...editorExtras } = editorData;
        setModelMeta({
          id: data.id,
          label: data.third_party_settings?.modeler_api?.label || data.id,
          editorExtras,
        });
        setLoadState('ready');
        setTimeout(() => fitView({ padding: 0.15 }), 50);
      })
      .catch(e => {
        console.error('ECA load error:', e);
        setLoadState('error');
      });
  }, [modelId, catalogueLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reprise du rangement automatique avec les dimensions RÉELLES : la passe du
  // chargement travaille sur des estimations (230×110) alors qu'un noeud
  // configuré en fait 250×181, ce qui laisse se chevaucher les noeuds d'un même
  // rang. On attend que ReactFlow ait mesuré tout le monde (le culling est
  // suspendu pendant ce temps, cf. `onlyRenderVisibleElements` plus bas) ; le
  // délai de garde évite de rester bloqué — donc sans culling — si un noeud
  // n'est jamais mesuré.
  const finishAutoLayout = useCallback(() => {
    setAutoLayoutPending(false);
    setNodes((nds) => getLayoutedElements(nds, edges).nodes);
    setTimeout(() => fitView({ padding: 0.15 }), 50);
  }, [edges, setNodes, fitView]);

  useEffect(() => {
    if (!autoLayoutPending) return;
    if (nodes.length > 0 && nodes.every((n) => n.measured?.width && n.measured?.height)) {
      finishAutoLayout();
      return;
    }
    const guard = setTimeout(finishAutoLayout, AUTO_LAYOUT_MEASURE_TIMEOUT);
    return () => clearTimeout(guard);
  }, [autoLayoutPending, nodes, finishAutoLayout]);

  // Sync selected node data on every render
  const getSelectedNode = () => nodes.find((n) => n.id === selectedNode?.id) ?? null;

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // ---------------------------------------------------------------------------
  // Variable state helpers (exposed via VariablesCtx)
  // ---------------------------------------------------------------------------

  const updateVariable = useCallback((varId, updates) => {
    setVariables(v => ({ ...v, [varId]: { ...(v[varId] ?? {}), ...updates } }));
  }, []);

  const duplicateVarRef = useCallback((nodeId) => {
    setNodes(nds => {
      const node = nds.find(n => n.id === nodeId);
      if (!node) return nds;
      const newId = `varnode_${node.data.varId}_ref_${Date.now()}`;
      return [...nds, {
        id: newId,
        type: 'variable',
        position: { x: node.position.x + 100, y: node.position.y + 50 },
        data: { varId: node.data.varId },
      }];
    });
    closeContextMenu();
  }, [setNodes, closeContextMenu]);

  const deleteVarRef = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    closeContextMenu();
  }, [setNodes, setEdges, closeContextMenu]);

  const deleteVariable = useCallback((varId) => {
    setNodes(nds => nds.filter(n => !(n.type === 'variable' && n.data.varId === varId)));
    // Filter edges connected to any reference of this variable (IDs contain varId)
    setEdges(eds => eds.filter(e => !e.source.includes(varId) && !e.target.includes(varId)));
    setVariables(v => { const { [varId]: _, ...rest } = v; return rest; });
    setSelectedNode(null);
    closeContextMenu();
  }, [setNodes, setEdges, closeContextMenu]);

  // Variables context value — memoized to avoid cascade re-renders
  const variablesCtxValue = useMemo(() => ({
    variables,
    updateVariable,
    duplicateVarRef,
    deleteVarRef,
    deleteVariable,
  }), [variables, updateVariable, duplicateVarRef, deleteVarRef, deleteVariable]);

  // ---------------------------------------------------------------------------
  // Add variable at a canvas position
  // ---------------------------------------------------------------------------

  const addVariable = useCallback((flowPosition) => {
    const varId   = genVarId();
    const nodeId  = `varnode_${varId}_ref_0`;
    const color   = varIdToColor(varId);
    setVariables(v => ({
      ...v,
      [varId]: { label: '', color, defaultValue: '' },
    }));
    setNodes(nds => [...nds, {
      id: nodeId,
      type: 'variable',
      position: flowPosition,
      data: { varId },
    }]);
    // Auto-select to open config panel
    setSelectedNode({ id: nodeId, type: 'variable', data: { varId } });
    closeContextMenu();
  }, [setNodes, setVariables, closeContextMenu]);

  // ---------------------------------------------------------------------------
  // Connections
  // ---------------------------------------------------------------------------

  // Intercept edge removals to clear the field value on the target node when
  // a data wire (data-in-*) is disconnected — otherwise fromEca reconstructs
  // the wire on reload because the token value is still in the configuration.
  const handleEdgesChange = useCallback((changes) => {
    const removals = changes.filter(c => c.type === 'remove');
    if (removals.length > 0) {
      setNodes(nds => {
        let updated = nds;
        for (const change of removals) {
          const edge = edges.find(e => e.id === change.id);
          if (!edge?.targetHandle?.startsWith('data-in-')) continue;
          const fieldKey = edge.targetHandle.replace('data-in-', '');
          updated = updated.map(n => {
            if (n.id !== edge.target) return n;
            return {
              ...n,
              data: {
                ...n.data,
                configuration: { ...n.data.configuration, [fieldKey]: '' },
              },
            };
          });
        }
        return updated;
      });
    }
    onEdgesChange(changes);
  }, [edges, onEdgesChange, setNodes]);

  const onConnect = useCallback(
    (params) => {
      const isDataHandle = (h) =>
        h && (h.startsWith('data-in') || h.startsWith('data-out') || h === 'var-in' || h === 'var-out');
      const isFlux = !isDataHandle(params.sourceHandle) && !isDataHandle(params.targetHandle);

      setEdges((eds) =>
        addEdge({ ...params, type: isFlux ? 'flux' : 'data' }, eds)
      );
    },
    [setEdges]
  );

  // ---------------------------------------------------------------------------
  // Node selection
  // ---------------------------------------------------------------------------

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
    closeContextMenu();
  }, [closeContextMenu]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    closeContextMenu();
  }, [closeContextMenu]);

  // ---------------------------------------------------------------------------
  // Context menus
  // ---------------------------------------------------------------------------

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, type: 'canvas', nodeId: null, varId: null });
  }, []);

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    if (node.type === 'variable') {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        type: 'variable',
        nodeId: node.id,
        varId: node.data.varId,
      });
    }
    // Other node types: suppress default browser menu only
  }, []);

  // ---------------------------------------------------------------------------
  // Drag-and-drop from picker
  // ---------------------------------------------------------------------------

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData('application/eca-node');
      if (!raw) return;
      const item = JSON.parse(raw);
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      addNodeAt(item, position);
    },
    [screenToFlowPosition]
  );

  // ---------------------------------------------------------------------------
  // Add standard node helper (from picker click or drop)
  // ---------------------------------------------------------------------------

  const addNodeAt = useCallback(
    (item, position) => {
      const id = genId(item.nodeType);
      const newNode = {
        id,
        type: item.nodeType,
        position: position ?? { x: 200 + Math.random() * 300, y: 200 + Math.random() * 200 },
        data: defaultNodeData(item.nodeType, item.pluginId, item.label),
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes]
  );

  // ---------------------------------------------------------------------------
  // Auto-layout
  // ---------------------------------------------------------------------------

  const onLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    // Let React re-render the new positions before fitting the view
    setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 50);
  }, [nodes, edges, setNodes, setEdges, fitView]);

  // ---------------------------------------------------------------------------
  // Export / Save
  // ---------------------------------------------------------------------------

  // Tout enregistrement depuis l'interface fige la mise en page affichée : la
  // prochaine ouverture la reprendra telle quelle au lieu de la recalculer.
  const editorExtrasToSave = () => ({ ...(modelMeta?.editorExtras ?? {}), layout: LAYOUT_MANUAL });

  const handleExport = () => {
    const model = toEca({ nodes, edges, variables, modelId: modelMeta?.id, modelLabel: modelMeta?.label, getPluginDef, editorExtras: editorExtrasToSave() });
    console.log('ECA JSON export:', JSON.stringify(model, null, 2));
    alert('ECA model exported to console (F12).');
  };

  const [saving, setSaving]           = useState(false);
  const [saveDialog, setSaveDialog]   = useState(false);
  const [newModelId, setNewModelId]   = useState('');
  const [newModelLabel, setNewModelLabel] = useState('');

  const doSave = async (id, label) => {
    setSaving(true);
    try {
      const model = toEca({ nodes, edges, variables, modelId: id, modelLabel: label || id, getPluginDef, editorExtras: editorExtrasToSave() });
      const tokenResp = await fetch(`${DRUPAL_BASE_URL}/session/token`, { credentials: 'include' });
      const csrfToken = await tokenResp.text();
      const resp = await fetch(
        `${DRUPAL_BASE_URL}/decoupled-config-api/save?entity_type=eca&id=${id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ data: model }),
        }
      );
      const result = await resp.json();
      if (result?.status?.type === 'error') throw new Error(result.status.message);
      // After first save, update meta so toolbar reflects the new id/label
      setModelMeta(m => ({ ...(m ?? {}), id, label: label || id }));
      // Embarqué, l'URL appartient au builder : y pousser celle de l'éditeur ECA
      // ARRACHERAIT la page en cours d'édition sous la pop-in.
      if (!embedded) {
        navigate(`/eca-editor/${id}`, { replace: true });
      }
    } catch (e) {
      alert(`Erreur de sauvegarde : ${e.message}`);
    } finally {
      setSaving(false);
      setSaveDialog(false);
    }
  };

  const handleSave = () => {
    if (modelMeta?.id === 'mock') {
      alert('Impossible de sauvegarder le modèle mock. Ouvrez un vrai modèle depuis la liste.');
      return;
    }
    if (!modelMeta?.id) {
      // New model — ask for ID first; seed the label from the toolbar field.
      if (modelMeta?.label) setNewModelLabel(modelMeta.label);
      setSaveDialog(true);
      return;
    }
    doSave(modelMeta.id, modelMeta.label);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Stable identity so the MiniMap (which re-renders every viewport frame while
  // panning) doesn't recreate this fn and re-color every node each frame.
  const miniMapNodeColor = useCallback((n) => {
    if (n.type === 'variable') {
      return variables[n.data?.varId]?.color ?? '#8B5CF6';
    }
    return MINIMAP_TYPE_COLORS[nodeColorKey(n.type, n.data?.plugin)] ?? '#9CA3AF';
  }, [variables]);

  const liveSelectedNode = getSelectedNode();

  // Full-screen loading / error overlay
  if (loadState === 'loading') {
    return (
      <div className="flex items-center justify-center w-full font-sans text-gray-400 text-sm gap-2" style={{ height: shellHeight }}>
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Chargement du modèle…
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center w-full font-sans gap-4" style={{ height: shellHeight }}>
        <div className="text-red-500 text-sm">Impossible de charger le modèle ECA.</div>
        <button onClick={() => goBack()} className="text-sm text-iris-600 underline">
          ← Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <VariablesCtx.Provider value={variablesCtxValue}>
      <div className="flex w-full overflow-hidden bg-gray-50 font-sans" style={{ height: shellHeight }}>
        {/* Left sidebar — plugin picker */}
        <EcaPluginPicker onAddNode={(item) => addNodeAt(item)} />

        {/* Canvas */}
        <div
          ref={reactFlowWrapper}
          className="flex-1 relative min-w-0 overflow-hidden"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          {/* Toolbar */}
          <div className="absolute top-3 left-3 right-3 z-10 flex flex-wrap items-center gap-2">
            <div className="bg-white rounded-lg shadow border border-gray-200 px-3 py-2 flex items-center gap-3">
              {/* Back link */}
              <button
                onClick={() => goBack()}
                className="text-gray-400 hover:text-gray-700 transition-colors"
                aria-label={embedded ? 'Fermer le processus' : 'Retour à la liste'}
                title={embedded ? 'Fermer le processus' : 'Retour à la liste'}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-xs text-gray-200">|</span>
              <input
                type="text"
                value={modelMeta?.label ?? ''}
                onChange={e => setModelMeta(m => ({ ...(m ?? { id: '' }), label: e.target.value }))}
                disabled={modelMeta?.id === 'mock'}
                placeholder="Nom du modèle"
                title={modelMeta?.id ? `Nom du modèle (${modelMeta.id})` : 'Nom du modèle'}
                className="text-sm font-bold text-gray-700 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-iris-500 focus:outline-none px-0.5 py-0.5 w-44 disabled:cursor-not-allowed"
              />
              <span className="text-xs text-gray-300">|</span>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-5 h-0.5 bg-blue-500" style={{ height: 3 }} />
                  Flux
                </span>
                <span className="flex items-center gap-1">
                  <span
                    className="inline-block w-5 bg-green-500"
                    style={{ height: 2, borderTop: '2px dashed #10B981' }}
                  />
                  Data
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-iris-500 opacity-80" />
                  Variable
                </span>
              </div>
            </div>
            <button
              onClick={onLayout}
              className="btn btn-secondary btn-sm"
              title="Reorganize nodes automatically (Dagre left→right)"
            >
              Auto-layout
            </button>
            <button
              onClick={handleExport}
              className="btn btn-secondary btn-sm"
            >
              Export JSON
            </button>
            {modelMeta?.id !== 'mock' && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary btn-sm"
              >
                {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            )}
          </div>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            connectionLineComponent={CustomConnectionLine}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onPaneContextMenu={onPaneContextMenu}
            onNodeContextMenu={onNodeContextMenu}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={FIT_VIEW_OPTIONS}
            // Suspendu le temps du rangement automatique : sans cela ReactFlow ne
            // mesure que les noeuds à l'écran, la 2e passe n'obtient jamais toutes
            // les hauteurs et le graphe reste rangé sur des estimations.
            onlyRenderVisibleElements={!autoLayoutPending}
            // Culls off-screen nodes/edges from the DOM so per-frame layout cost
            // during pan/drag scales with VISIBLE nodes, not the whole graph.
            // Benchmarked (panPerf.browser.test.jsx, N=600): pan 7.1ms → 0.4ms,
            // drag 11ms → 2.4ms per step. See that file to re-measure.
            deleteKeyCode={DELETE_KEY_CODES}
            minZoom={0.3}
            maxZoom={2}
            defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
            className="bg-gray-50"
          >
            <ZoomScaleBridge />
            <Background gap={20} size={1} color="#e5e7eb" />
            <Controls />
            <MiniMap
              nodeColor={miniMapNodeColor}
              maskColor="rgba(255,255,255,0.6)"
            />
          </ReactFlow>

          {/* Context menu */}
          <ContextMenu
            menu={contextMenu}
            onAddVariable={() => {
              const flowPos = screenToFlowPosition({ x: contextMenu.x, y: contextMenu.y });
              addVariable(flowPos);
            }}
            onDuplicateRef={() => duplicateVarRef(contextMenu.nodeId)}
            onDeleteRef={() => deleteVarRef(contextMenu.nodeId)}
            onDeleteVariable={() => deleteVariable(contextMenu.varId)}
            onClose={closeContextMenu}
          />
        </div>

        {/* Right sidebar — config panel */}
        {liveSelectedNode && (
          <NodeConfigPanel
            node={liveSelectedNode}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </div>

      {/* New model save dialog */}
      {saveDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.35)' }}
          onClick={() => setSaveDialog(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl p-6 w-96 font-sans"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-base font-bold text-gray-900 mb-4">Créer un nouveau modèle ECA</h2>

            <label className="field-label text-xs mb-1">
              Identifiant machine <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              type="text"
              placeholder="ex: mon_workflow"
              value={newModelId}
              onChange={e => setNewModelId(e.target.value.replace(/[^a-z0-9_]/g, '_').toLowerCase())}
              className="input mb-1"
            />
            <p className="text-xs text-gray-400 mb-4">
              Lettres minuscules, chiffres et underscores uniquement.
            </p>

            <label className="field-label text-xs mb-1">Label</label>
            <input
              type="text"
              placeholder="ex: Notification à la création"
              value={newModelLabel}
              onChange={e => setNewModelLabel(e.target.value)}
              className="input mb-5"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSaveDialog(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  if (!newModelId.trim()) return;
                  doSave(newModelId.trim(), newModelLabel.trim());
                }}
                disabled={!newModelId.trim() || saving}
                className="btn btn-primary"
              >
                {saving ? 'Sauvegarde…' : 'Créer et sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </VariablesCtx.Provider>
  );
};

// ---------------------------------------------------------------------------
// Public export — wraps providers
// ---------------------------------------------------------------------------

export const EcaEditor = (props) => (
  <EcaPluginCatalogueProvider>
    <ReactFlowProvider>
      <ExecutionMockProvider>
        <EcaEditorInner {...props} />
      </ExecutionMockProvider>
    </ReactFlowProvider>
  </EcaPluginCatalogueProvider>
);
